import React, { createContext, useContext, useReducer, ReactNode } from "react";

export type ToastVariant = "success" | "error" | "warning" | "info";

export interface Toast {
  id: string;
  title: string | ReactNode;
  description?: string;
  variant: ToastVariant;
  duration?: number; // in milliseconds, if undefined it will stay until dismissed
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface ToastContextType {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, "id">) => void;
  removeToast: (id: string) => void;
}

const DEFAULT_TOAST_DURATION = 3500;

const ToastContext = createContext<ToastContextType | undefined>(undefined);

interface ToastAction {
  type: "ADD_TOAST" | "REMOVE_TOAST";
  payload: any;
}

const toastReducer = (state: Toast[], action: ToastAction): Toast[] => {
  switch (action.type) {
    case "ADD_TOAST":
      return [...state, action.payload];
    case "REMOVE_TOAST":
      return state.filter((toast) => toast.id !== action.payload);
    default:
      return state;
  }
};

export const ToastProvider = ({ children }: { children: React.ReactNode }) => {
  const [toasts, dispatch] = useReducer(toastReducer, []);

  const addToast = (toast: Omit<Toast, "id">) => {
    const id = Math.random().toString(36).substr(2, 9);
    dispatch({ type: "ADD_TOAST", payload: { ...toast, id } });

    const toastDuration =
      toast.duration !== undefined ? toast.duration : DEFAULT_TOAST_DURATION;

    setTimeout(() => {
      removeToast(id);
    }, toastDuration);
  };

  // @ts-ignore
  window.addToast = addToast;

  const removeToast = (id: string) => {
    dispatch({ type: "REMOVE_TOAST", payload: id });
  };

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};
